import { ConfigProvider, Divider, Segmented } from "antd";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

const SEGMENTED_THEME = {
    components: {
        Segmented: {
            itemSelectedBg: "#5d5dff",
            itemSelectedColor: "#ffffff",
            trackPadding: "5px",
        },
    },
};

const getTabFromURL = (urlParam, defaultValue, validValues) => {
    const param = new URLSearchParams(window.location.search).get(urlParam);
    if (param && (!validValues || validValues.includes(param))) {
        return param;
    }
    return defaultValue;
};

const updateTabURL = (urlParam, value) => {
    const url = new URL(window.location);
    url.searchParams.set(urlParam, value);
    window.history.pushState({}, "", url);
};

export const useSegmentedTabState = ({
    defaultValue,
    urlParam = "tab",
    syncWithUrl = true,
    validValues,
}) => {
    const [activeTab, setActiveTabState] = useState(() =>
        syncWithUrl ? getTabFromURL(urlParam, defaultValue, validValues) : defaultValue,
    );

    useEffect(() => {
        if (!syncWithUrl) {
            return undefined;
        }

        const handlePopState = () => {
            setActiveTabState(getTabFromURL(urlParam, defaultValue, validValues));
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, [syncWithUrl, urlParam, defaultValue, validValues]);

    const setActiveTab = (value) => {
        setActiveTabState(value);
        if (syncWithUrl) {
            updateTabURL(urlParam, value);
        }
    };

    const handleTabChange = (value) => {
        setActiveTab(value);
    };

    return { activeTab, setActiveTab, handleTabChange };
};

const CustomSegmentedTabs = ({
    items,
    value,
    onChange,
    title,
    showHeader = false,
    showDivider = true,
    className = "",
    segmentedClassName = "bg-white mb-4",
}) => {
    const options = items.map(({ label, value: itemValue, disabled }) => ({
        label,
        value: itemValue,
        disabled,
    }));

    const activeContent = items.find((item) => item.value === value)?.content;
    const hasTabContent = items.some((item) => item.content);

    return (
        <div className={className}>
            {showHeader && title ? (
                <>
                    <div className="flex flex-row gap-4">
                        <div className="flex-1 text-[1.2rem] fontbold">{title}</div>
                    </div>
                    {showDivider ? <Divider variant="solid" className="border-gray-300" /> : null}
                </>
            ) : null}

            <ConfigProvider theme={SEGMENTED_THEME}>
                <Segmented
                    value={value}
                    onChange={onChange}
                    options={options}
                    block
                    shape="round"
                    className={segmentedClassName}
                />
            </ConfigProvider>

            {hasTabContent ? activeContent : null}
        </div>
    );
};

CustomSegmentedTabs.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.node.isRequired,
            value: PropTypes.string.isRequired,
            content: PropTypes.node,
            disabled: PropTypes.bool,
        }),
    ).isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    title: PropTypes.node,
    showHeader: PropTypes.bool,
    showDivider: PropTypes.bool,
    className: PropTypes.string,
    segmentedClassName: PropTypes.string,
};

export default CustomSegmentedTabs;
